import React, { useState } from 'react';
import { MessageCircle, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

// Static puppy data
const PUPPIES_DATA = [
  {
    id: "1",
    breed: "Golden Retriever",
    age: "8-12 weeks",
    description: "Friendly, intelligent, and devoted. Perfect family companion with gentle temperament. Great with kids and highly trainable.",
    temperament: "Friendly, Intelligent, Devoted",
    vaccinated: true,
    images: ["https://customer-assets.emergentagent.com/job_petsmart-hub/artifacts/3bjx16yv_Golden%20Retriever.jpeg"],
    available: true,
    featured: true
  },
  {
    id: "2",
    breed: "Shih Tzu",
    age: "8-12 weeks",
    description: "Affectionate, playful, and outgoing. Excellent apartment dog with beautiful long coat. Perfect companion for families.",
    temperament: "Affectionate, Playful, Outgoing",
    vaccinated: true,
    images: ["https://customer-assets.emergentagent.com/job_petsmart-hub/artifacts/adz6fgom_Shih%20Tzus.jpg"],
    available: true,
    featured: true
  },
  {
    id: "3",
    breed: "Maltipoo",
    age: "8-12 weeks",
    description: "Gentle, affectionate, and active. Low-shedding designer breed that some people with mild allergies tolerate well. Very smart and easy to train.",
    temperament: "Gentle, Affectionate, Active",
    vaccinated: true,
    images: ["https://customer-assets.emergentagent.com/job_petsmart-hub/artifacts/8ae7wm6v_Maltipoo.jpeg"],
    available: true,
    featured: true
  },
  {
    id: "4",
    breed: "Poodle",
    age: "8-12 weeks",
    description: "Highly intelligent, active, and elegant. Known for their low-shedding coat and exceptional trainability. Great for active families.",
    temperament: "Intelligent, Active, Elegant",
    vaccinated: true,
    images: ["https://customer-assets.emergentagent.com/job_petsmart-hub/artifacts/rmqfvf52_poodle.jpeg"],
    available: true,
    featured: false
  },
  {
    id: "5",
    breed: "Toy Pom",
    age: "8-12 weeks",
    description: "Bold, lively, and intelligent. Small size with big personality. Perfect lap dog and excellent companion for apartments.",
    temperament: "Bold, Lively, Intelligent",
    vaccinated: true,
    images: ["https://customer-assets.emergentagent.com/job_petsmart-hub/artifacts/k7y6509d_toy%20pom.jpg"],
    available: true,
    featured: false
  },
  {
    id: "6",
    breed: "Maltese",
    age: "8-12 weeks",
    description: "Gentle, playful, and fearless. Beautiful white coat and sweet personality. Ideal for families and seniors alike.",
    temperament: "Gentle, Playful, Fearless",
    vaccinated: true,
    images: ["https://customer-assets.emergentagent.com/job_petsmart-hub/artifacts/k3z6byfw_Maltese.jpeg"],
    available: true,
    featured: false
  },
  {
    id: "7",
    breed: "Husky",
    age: "8-12 weeks",
    description: "Loyal, outgoing, and mischievous. Beautiful blue eyes and striking appearance. Energetic breed perfect for active families.",
    temperament: "Loyal, Outgoing, Mischievous",
    vaccinated: true,
    images: ["https://customer-assets.emergentagent.com/job_petsmart-hub/artifacts/kvfrnsns_Husky.jpeg"],
    available: true,
    featured: false
  },
  {
    id: "8",
    breed: "Akita",
    age: "8-12 weeks",
    description: "Dignified, courageous, and loyal. Noble Japanese breed with impressive presence. Excellent guard dog and devoted family companion.",
    temperament: "Dignified, Courageous, Loyal",
    vaccinated: true,
    images: ["https://customer-assets.emergentagent.com/job_petsmart-hub/artifacts/ntnfjjg0_Akita.jpeg"],
    available: true,
    featured: true
  },
  {
    id: "9",
    breed: "German Shepherd",
    age: "8-12 weeks",
    description: "Confident, courageous, and smart. Excellent working dog and loyal family protector. Highly trainable and versatile breed.",
    temperament: "Confident, Courageous, Smart",
    vaccinated: true,
    images: ["https://customer-assets.emergentagent.com/job_petsmart-hub/artifacts/17ea0b93_German%20shepherd.jpeg"],
    available: true,
    featured: true
  },
  {
    id: "10",
    breed: "Labrador",
    age: "8-12 weeks",
    description: "Outgoing, even-tempered, and gentle. Most popular family dog in the world. Friendly with everyone and great with children.",
    temperament: "Outgoing, Even Tempered, Gentle",
    vaccinated: true,
    images: ["https://customer-assets.emergentagent.com/job_petsmart-hub/artifacts/np0pkanr_Labrador.jpeg"],
    available: true,
    featured: true
  }
];

const Puppies = () => {
  const [puppies] = useState(PUPPIES_DATA);
  const [loading] = useState(false);

  const handleReserve = (breed) => {
    const message = `Hello, I am interested in the ${breed} puppy. Please share details about availability and price.`;
    window.open(`https://wa.me/917217648679?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div data-testid="puppies-page" style={{ backgroundColor: 'var(--neutral-bg)', minHeight: '100vh' }}>
      <SEO />
      {/* Hero Section */}
      <section
        className="py-20"
        style={{
          background: 'linear-gradient(to bottom right, #E0F2FE, #FFFFFF)',
          borderBottom: '1px solid var(--neutral-border)'
        }}
      >
        <div className="container mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ color: 'var(--text-main)' }}>
            Available Puppies
          </h1>
          <p className="text-lg mb-6" style={{ color: 'var(--text-muted)' }}>
            Find your perfect furry companion from our healthy, vaccinated puppies
          </p>

          {/* All Breeds Available Notice */}
          <div
            className="inline-block p-6 rounded-2xl mb-4"
            style={{
              backgroundColor: 'var(--primary-light)',
              border: '2px solid var(--primary-main)'
            }}
          >
            <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--primary-dark)' }}>
              All Dog Breeds Available
            </h3>
            <p style={{ color: 'var(--text-main)' }}>
              If you are looking for a specific breed, please contact us. We can arrange most dog breeds on request.
            </p>
            <button
              onClick={() => {
                const message = 'Hello, I am looking for a specific dog breed. Can you help me?';
                window.open(`https://wa.me/917217648679?text=${encodeURIComponent(message)}`, '_blank');
              }}
              className="mt-4 transition-all hover:-translate-y-1"
              style={{
                backgroundColor: 'var(--primary-main)',
                color: 'white',
                padding: '0.75rem 1.5rem',
                borderRadius: '9999px',
                fontWeight: 'bold',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              Request Your Breed
            </button>
          </div>
        </div>
      </section>

      {/* Puppies Grid */}
      <section className="py-16">
        <div className="container mx-auto">
          {loading ? (
            <div className="text-center py-20">
              <p style={{ color: 'var(--text-muted)' }}>Loading puppies...</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="puppies-list">
              {puppies.map((puppy, index) => (
                <motion.div
                  key={puppy.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="shadow-soft transition-all hover:shadow-xl group"
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '1.5rem',
                    overflow: 'hidden'
                  }}
                >
                  <div style={{ position: 'relative', overflow: 'hidden' }}>
                    <img
                      src={puppy.images[0]}
                      alt={puppy.breed}
                      style={{
                        width: '100%',
                        height: '280px',
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease'
                      }}
                      className="group-hover:scale-110"
                    />
                    <div
                      style={{
                        position: 'absolute',
                        top: '1rem',
                        left: '1rem',
                        backgroundColor: puppy.available ? 'var(--accent-main)' : '#EF4444',
                        color: 'white',
                        padding: '0.5rem 1rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: 'bold'
                      }}
                    >
                      {puppy.available ? 'Available' : 'Reserved'}
                    </div>
                    {puppy.vaccinated && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '1rem',
                          right: '1rem',
                          backgroundColor: 'var(--primary-main)',
                          color: 'white',
                          padding: '0.5rem 1rem',
                          borderRadius: '9999px',
                          fontSize: '0.75rem',
                          fontWeight: 'bold'
                        }}
                      >
                        Vaccinated
                      </div>
                    )}
                  </div>

                  <div style={{ padding: '1.5rem' }}>
                    <h3 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-main)' }}>
                      {puppy.breed}
                    </h3>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold" style={{ color: 'var(--text-muted)' }}>
                          Age:
                        </span>
                        <span className="text-sm" style={{ color: 'var(--text-main)' }}>
                          {puppy.age}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold" style={{ color: 'var(--text-muted)' }}>
                          Temperament:
                        </span>
                        <span className="text-sm" style={{ color: 'var(--text-main)' }}>
                          {puppy.temperament}
                        </span>
                      </div>
                    </div>

                    <p className="text-sm mb-4" style={{ color: 'var(--text-muted)', lineHeight: '1.6' }}>
                      {puppy.description}
                    </p>

                    {puppy.vaccinated && (
                      <div
                        className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg"
                        style={{ backgroundColor: 'var(--accent-light)' }}
                      >
                        <Check size={16} style={{ color: 'var(--accent-main)' }} />
                        <span className="text-sm font-medium" style={{ color: 'var(--accent-main)' }}>
                          Fully Vaccinated & Health Certified
                        </span>
                      </div>
                    )}

                    <button
                      onClick={() => handleReserve(puppy.breed)}
                      data-testid={`reserve-puppy-button-${puppy.id}`}
                      disabled={!puppy.available}
                      className="w-full transition-all hover:-translate-y-1 flex items-center justify-center gap-2"
                      style={{
                        backgroundColor: puppy.available ? 'var(--primary-main)' : '#9CA3AF',
                        color: 'white',
                        padding: '0.875rem',
                        borderRadius: '0.75rem',
                        fontWeight: 'bold',
                        border: 'none',
                        cursor: puppy.available ? 'pointer' : 'not-allowed',
                        opacity: puppy.available ? 1 : 0.6
                      }}
                    >
                      <MessageCircle size={18} />
                      {puppy.available ? 'Reserve Puppy on WhatsApp' : 'Currently Reserved'}
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16" style={{ backgroundColor: 'white' }}>
        <div className="container mx-auto text-center">
          <div
            className="inline-block p-12 rounded-3xl"
            style={{ backgroundColor: 'var(--primary-light)' }}
          >
            <h2 className="text-3xl font-bold mb-4" style={{ color: 'var(--text-main)' }}>
              Can't Find What You're Looking For?
            </h2>
            <p className="mb-6" style={{ color: 'var(--text-muted)' }}>
              Contact us for special breed requests or upcoming litters
            </p>
            <button
              onClick={() => {
                const message = 'Hello, I am looking for a specific puppy breed. Can you help?';
                window.open(`https://wa.me/917217648679?text=${encodeURIComponent(message)}`, '_blank');
              }}
              data-testid="special-request-button"
              className="transition-all hover:-translate-y-1"
              style={{
                backgroundColor: 'var(--primary-main)',
                color: 'white',
                padding: '1rem 2rem',
                borderRadius: '9999px',
                fontWeight: 'bold',
                border: 'none',
                cursor: 'pointer',
                boxShadow: '0 10px 30px rgba(14, 165, 233, 0.3)'
              }}
            >
              Contact Us for Special Requests
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Puppies;